import { CommonModule } from '@angular/common';
import type { OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { Component, HostBinding, Input } from '@angular/core';
import { AccessibilityService } from '@core-mkt/services/accessibility/accessibility.service';
import { IconService } from '@core-mkt/services/iconService/icon.service';
import { QueryParamDirective } from '@core-mkt/services/query-param/query-param.directive';
import { CheckoutUrlBuilderService } from '@core-mkt/shared/services/checkout-url-builder/checkout-url-builder.service';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { VisionButton } from './vision-button';

@Component({
  selector: 'vision-button',
  templateUrl: './vision-button.component.html',
  styleUrls: [],
  standalone: true,
  imports: [CommonModule, FontAwesomeModule, QueryParamDirective],
})
export class VisionButtonComponent implements OnInit, OnChanges {
  @Input() defaultStyle = 'primary'; // This property can be used in case there's no value coming from craft
  @Input() defaultText: string; // Same as defaultStyle
  @Input() className?: string; // Extra classes that can be added to the button
  @Input() data: VisionButton; // Description of the values on the interface file.
  @Input() disabled = false; // disable the button
  @HostBinding('class.w-full') get isFullWidth(): boolean {
    return this.data.fullWidth;
  }
  @HostBinding('class.mx-auto') get isCenterAligned(): boolean {
    return this.data.buttonAlignment === 'center';
  }
  @HostBinding('class.mr-auto') get isLeftAligned(): boolean {
    return this.data.buttonAlignment === 'left';
  }
  @HostBinding('class.ml-auto') get isRightAligned(): boolean {
    return this.data.buttonAlignment === 'right';
  }
  readonly definedStyles = ['primary', 'secondary', 'tertiary'];
  overridedButtonLink: string;
  linkIcon = null;
  relValues = [];

  constructor(
    private iconService: IconService,
    private accessibilityService: AccessibilityService,
    private checkoutUrlBuilderService: CheckoutUrlBuilderService,
  ) {}

  get styleClass(): string {
    if (this.disabled) {
      return 'disabled';
    }
    // Gets the style class that is going to be set on the HTML
    //this is broken down into steps for easier debugging
    const agnosticStyles = this.definedStyles.includes(this.data.buttonStyle)
      ? this.data.buttonStyle
      : this.defaultStyle;
    return agnosticStyles;
  }

  get text(): string {
    return this.data.buttonText ? this.data.buttonText : this.defaultText;
  }

  get buttonSizeClass(): string {
    switch (this.data.buttonSize) {
      case 'small':
        return 'text-base';
      case 'large':
        return 'text-md';
      default:
        return 'w-min whitespace-nowrap';
    }
  }

  //If href is null or undefined, it should do nothing
  get buttonLink(): string {
    return this.overridedButtonLink;
  }

  get useIcon(): boolean {
    return this.data.useIcon !== undefined && this.data.useIcon;
  }

  get buttonStyle(): string {
    if (this.disabled) {
      return 'disabled';
    }
    // Gets the selected style for the button. or the default one.
    return this.definedStyles.includes(this.data.buttonStyle) ? this.data.buttonStyle : this.defaultStyle;
  }

  get rel(): string {
    return this.relValues.join(' ');
  }

  get target(): string {
    return this.data.newTab ? '_blank' : '';
  }

  get dataFormElementId(): string {
    return this.data.ctaTag ? this.data.ctaTag : '';
  }

  ngOnInit(): void {
    if (this.data.nofollow) {
      this.relValues.push('nofollow');
    }
    if (this.data.noreferrer) {
      this.relValues.push('noreferrer');
    }
    if (this.data.noopener) {
      this.relValues.push('noopener');
    }

    const iconRes = this.iconService.getBrandIcon('resourcesLinkIcon');

    if (iconRes) {
      import('@fortawesome/free-solid-svg-icons')
        .then((fa) => {
          this.linkIcon = fa[iconRes.icon];
        })
        .catch(() => {
          this.linkIcon = null;
        });
    }
    this.setButtonLink();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      this.setButtonLink();
    }
  }

  /**
   * Gets the aria-label value to the anchor element
   * From the link URL
   * Removing dashes and slashes from a link
   * @param link The string with the URL
   */
  getAriaLabelFromUrlLink(link: string): string {
    const sectionList = link?.split('/');
    let indexSection = sectionList.length - 1;
    if (sectionList[indexSection] === '') {
      indexSection = sectionList.length - 2;
    }

    const lastSection = sectionList[indexSection];
    const textAriaLabel = lastSection?.replace(/-/gi, ' ').trim();
    return textAriaLabel?.replace(/(^\w{1})|(\s+\w{1})/g, (l) => l.toUpperCase());
  }

  getIdToAriaLabelledbyContext(label: string, link: string): string {
    const cleanText = this.accessibilityService.getAriaLabelledby(label);
    const urlContext = this.getTextContext(link);
    const cleanUrlContext = this.accessibilityService.getAriaLabelledby(urlContext);
    const ctaContext = this.accessibilityService.getAriaLabelledby(this.dataFormElementId);
    return [cleanText, cleanUrlContext, ctaContext, 'context'].filter(Boolean).join('-');
  }

  getTextContext(link: string): string {
    if (link?.startsWith('https://checkout') && this.data.buttonAriaLabel) {
      return this.data.buttonAriaLabel;
    }

    if (link?.startsWith('http')) {
      return this.getAriaLabelFromUrlLink(link);
    }
    return this.accessibilityService.getCompleteTextContextForButton(link);
  }

  setButtonLink(): void {
    // Normalize the host first: a non-checkout handoff (AARP's course-selection) skips the builder below.
    const originalLink = this.checkoutUrlBuilderService.toEnvCheckoutHost(this.data.buttonLink || '#');

    // Use enhanced coupon aggregation for checkout URLs
    if (originalLink.toLowerCase().includes('checkout')) {
      this.overridedButtonLink = this.checkoutUrlBuilderService.buildCheckoutUrlWithAllCoupons(originalLink);
    } else {
      this.overridedButtonLink = originalLink;
    }
  }

  onClick(event: Event): void {
    if (this.disabled) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
  }
}
